import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { exportReport } from '../../api/analytics';
import { useToast } from '../../context/ToastContext';
import DatePicker from '../ui/DatePicker';

const REPORT_TYPES = [
  { value: 'appointments', labelKey: 'salon.analytics.export.types.appointments' },
  { value: 'orders', labelKey: 'salon.analytics.export.types.orders' },
  { value: 'income', labelKey: 'salon.analytics.export.types.income' },
  { value: 'pos', labelKey: 'salon.analytics.export.types.pos' },
  { value: 'inventory', labelKey: 'salon.analytics.export.types.inventory' },
];

const ExportReportModal = ({ open, onClose, branches, defaultBranchId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [branchId, setBranchId] = useState(defaultBranchId || 'all');
  const [type, setType] = useState('income');
  const [format, setFormat] = useState('csv');

  const exportMut = useMutation({
    mutationFn: () =>
      exportReport({
        startDate,
        endDate,
        branchId: branchId === 'all' ? undefined : branchId,
        type,
        format,
      }),
    onSuccess: () => {
      showToast(t('toast.reportDownloaded'), 'success');
      onClose();
    },
    onError: () => showToast(t('toast.exportFailed'), 'error'),
  });

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-6">
        <h2 className="text-lg font-semibold">{t('salon.analytics.export.title')}</h2>
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DatePicker
              id="export-start"
              label={t('salon.analytics.export.startDate')}
              compact
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <DatePicker
              id="export-end"
              label={t('salon.analytics.export.endDate')}
              compact
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.branch')}</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1.5 text-sm">
              <option value="all">{t('salon.products.allBranches')}</option>
              {(branches || []).map((b) => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('salon.analytics.export.reportType')}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {REPORT_TYPES.map((reportType) => (
                <button
                  key={reportType.value}
                  type="button"
                  onClick={() => setType(reportType.value)}
                  className={`rounded-lg px-3 py-1.5 text-xs ${type === reportType.value ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'}`}
                >
                  {t(reportType.labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('salon.analytics.export.format')}</label>
            <div className="mt-2 flex gap-2">
              {['csv', 'pdf'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`rounded-lg px-3 py-1.5 text-xs uppercase ${format === f ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-2 text-sm">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!startDate || !endDate || exportMut.isPending}
            onClick={() => exportMut.mutate()}
            className="flex-1 rounded-lg bg-glow-600 py-2 text-sm text-white disabled:opacity-50"
          >
            {exportMut.isPending ? t('salon.analytics.export.generating') : t('salon.analytics.export.generateDownload')}
          </button>
        </div>
      </div>
    </>
  );
};

export default ExportReportModal;
